import { useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Pill from "@/components/Pill";
import ConfirmDialog from "@/components/ConfirmDialog";
import ResponsiveDataView from "@/components/responsive/ResponsiveDataView";
import ResponsivePageHeader from "@/components/responsive/ResponsivePageHeader";
import { formatDateTime, useLanguage, useTranslation } from "@/i18n/LanguageContext";
import { UserMinus } from "lucide-react";
import { toast } from "sonner";
import usePaginatedResource from "@/hooks/usePaginatedResource";

export default function Members() {
  const { role, user } = useAuth();
  const t = useTranslation();
  const { language } = useLanguage();
  const isOwner = role === "owner";
  const { page, loading, reload, changePage } = usePaginatedResource(
    async (params, { signal }) => (await api.get("/members", { params, signal })).data,
    { onError: (err) => toast.error(extractApiError(err)) },
  );
  const [confirmRemove, setConfirmRemove] = useState(null);

  const remove = async (member) => {
    try {
      await api.delete(`/members/${member.userId}`);
      setConfirmRemove(null);
      reload();
      toast.success(t("members.toast.removed"));
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  const isSelf = (m) => m.userId === user?.id;
  const roleBadge = (m) => <Pill variant={m.role}>{t(`common.states.${m.role}`)}</Pill>;

  return (
    <div data-testid="members-page" className="space-y-6">
      <ResponsivePageHeader
        title={t("members.title")}
        description={t("members.description")}
      />

      <ResponsiveDataView
        items={page.items}
        loading={loading}
        emptyState={t("members.empty")}
        getRowKey={(m) => m.userId}
        getRowTestId={(m) => `member-row-${m.userId}`}
        tableTestId="members-table"
        emptyTestId="members-empty"
        columns={[
          {
            key: "email",
            header: t("members.columns.email"),
            cell: (m) => (
              <>
                {m.email}
                {isSelf(m) && <span className="mono-eyebrow ml-2 !text-[9px]">{t("common.states.you")}</span>}
              </>
            ),
          },
          { key: "role", header: t("members.columns.role"), cell: roleBadge },
          { key: "joined", header: t("members.columns.joined"), cell: (m) => <span className="font-mono text-[11px] text-muted-foreground">{formatDateTime(m.createdAt, language)}</span> },
        ]}
        mobileTitle={(m) => m.email}
        mobileBadge={roleBadge}
        mobileFields={[
          { label: t("common.fields.role"), value: (m) => t(`common.states.${m.role}`) },
          { label: t("common.fields.joined"), value: (m) => formatDateTime(m.createdAt, language), priority: "secondary" },
          { label: t("common.fields.account"), value: (m) => isSelf(m) ? t("common.states.you") : t("common.states.member"), priority: "secondary" },
        ]}
        rowActions={[
          {
            key: "remove",
            label: t("common.actions.remove"),
            icon: UserMinus,
            destructive: true,
            onClick: setConfirmRemove,
            hidden: (m) => !isOwner || isSelf(m) || m.role === "owner",
            testId: (m) => `remove-member-${m.userId}`,
          },
        ]}
        pagination={{
          total: page.total,
          limit: page.limit,
          offset: page.offset,
          onChange: changePage,
          testId: "members-pager",
        }}
      />

      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(o) => !o && setConfirmRemove(null)}
        title={t("members.remove.title")}
        description={t("members.remove.description", { email: confirmRemove?.email })}
        confirmLabel={t("common.actions.remove")}
        destructive
        onConfirm={() => remove(confirmRemove)}
        testId="member-remove-dialog"
      />
    </div>
  );
}
